import * as Sentry from '@sentry/react';

import { Box, CircularProgress, Grid, IconButton, Typography } from '@mui/material';
import { Check, Close, GetApp } from '@mui/icons-material';
import { getDateFromString, getDateString } from 'utils';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';

import ApiContext from 'contexts/apiContext';
import ErrorContext from 'contexts/errorContext';
import { Invoice } from 'types/invoice';
import Paper from '@mui/material/Paper';
import Spinner from 'common/Spinner';
import StoreContext from 'contexts/storeContext';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import { createAndDownloadPdf } from 'pdf';

interface InvoicesTableProps {
  showUnpaidOnly?: boolean;
}

export default function InvoicesTable({ showUnpaidOnly = false }: InvoicesTableProps) {
  const { api } = useContext(ApiContext);
  const { store, formatNumber } = useContext(StoreContext);
  const { setError } = useContext(ErrorContext);

  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [pageCount, setPageCount] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [currentPage, setCurrentPage] = useState(1);
  const [downloadingInvoiceIds, setDownloadingInvoiceIds] = useState<Set<string>>(new Set());
  const previousShowUnpaidOnlyRef = useRef(showUnpaidOnly);

  const fetchInvoices = useCallback(
    (showLoader = true, pageNumber = 1, rows = rowsPerPage) => {
      setLoading(showLoader);
      const params: { page: number; page_size: number; unpaid_only: boolean } = {
        page: pageNumber,
        page_size: rows,
        unpaid_only: showUnpaidOnly,
      };
      api.invoices
        .list(store.id, params)
        .then((res) => {
          setInvoices(res.data.results);
          setPageCount(res.data.count);
        })
        .catch(setError)
        .finally(() => setLoading(false));
    },
    [api.invoices, rowsPerPage, setError, showUnpaidOnly, store.id],
  );

  const handleChangePage = (event: any, page: number) => {
    setCurrentPage(page + 1);
  };

  const handleChangeRowsPerPage = (event: any) => {
    const rows = parseInt(event.target.value, 10);
    setRowsPerPage(rows);
    setCurrentPage(1);
  };

  useEffect(() => {
    const showUnpaidOnlyChanged = previousShowUnpaidOnlyRef.current !== showUnpaidOnly;
    previousShowUnpaidOnlyRef.current = showUnpaidOnly;

    // When the unpaid filter toggles, reset once to page 1 and avoid fetching the stale previous page.
    if (showUnpaidOnlyChanged && currentPage !== 1) {
      setCurrentPage(1);
      return;
    }
    fetchInvoices(true, currentPage);
  }, [currentPage, fetchInvoices, showUnpaidOnly]);

  const downloadInvoice = (invoice: Invoice) => {
    setDownloadingInvoiceIds((prev) => new Set(prev).add(invoice.id));
    api.invoices
      .getPdf(store.id, invoice.id)
      .then((res) => {
        createAndDownloadPdf(document, res.data, `factura_${invoice.id}`);
      })
      .catch((error) => {
        setError(error);
        console.error(error);
        Sentry.captureException(error);
      })
      .finally(() => {
        setDownloadingInvoiceIds((prev) => {
          const next = new Set(prev);
          next.delete(invoice.id);
          return next;
        });
      });
  };

  return loading ? (
    <Box marginX={'auto'}>
      <Spinner />
    </Box>
  ) : invoices.length > 0 ? (
    <TableContainer component={Paper}>
      <Table aria-label='simple table'>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>Fecha</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Período</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Tipo</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Pedidos con pago</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Pagada</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Saldo</TableCell>
            <TableCell></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {invoices.map((invoice) => (
            <TableRow key={invoice.id}>
              <TableCell>{getDateString(getDateFromString(invoice.created_at))}</TableCell>
              <TableCell>{`${getDateString(getDateFromString(invoice.period_from), undefined, true)} - ${getDateString(
                getDateFromString(invoice.period_to),
                undefined,
                true,
              )}`}</TableCell>
              <TableCell>{invoice.amount ? `$${formatNumber(invoice.amount)}` : 'Ver factura'}</TableCell>
              <TableCell>{invoice.payment_type_display}</TableCell>
              <TableCell>
                {invoice.in_app_payment_only ? 'Online' : invoice.in_store_payment_only ? 'En tienda' : 'Online y en tienda'}
              </TableCell>
              <TableCell>
                {invoice.is_fully_paid ? <Check color='success' fontSize='small' /> : <Close color='error' fontSize='small' />}
              </TableCell>
              <TableCell>{invoice.remaining_amount ? `$${formatNumber(invoice.remaining_amount)}` : '-'}</TableCell>
              <TableCell>
                {downloadingInvoiceIds.has(invoice.id) ? (
                  <CircularProgress size={20} />
                ) : (
                  <IconButton
                    onClick={() => downloadInvoice(invoice)}
                    aria-label='Descargar factura'
                    color='primary'
                    size='small'
                    sx={{ p: 0 }}
                  >
                    <GetApp fontSize='small' />
                  </IconButton>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <TablePagination
        rowsPerPageOptions={[5, 10, 20]}
        component='div'
        count={pageCount}
        rowsPerPage={rowsPerPage}
        page={currentPage - 1}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage='Filas por página:'
        labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count !== -1 ? count : `más de ${to}`}`}
      />
    </TableContainer>
  ) : (
    <Grid container justifyContent='center'>
      <Typography>
        {showUnpaidOnly ? 'No tienes facturas pendientes.' : 'Aún no tienes facturas.'}
      </Typography>
    </Grid>
  );
}
